import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

function hexToHsl(hex) {
  const raw = (hex || "#C5A059").replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(raw)) return "40 48% 56%";
  let r = parseInt(raw.slice(0, 2), 16) / 255, g = parseInt(raw.slice(2, 4), 16) / 255, b = parseInt(raw.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [appearance, setAppearance] = useState({ app_name: "MB Property Prospect Finder", theme: "dark", primary_color: "#C5A059", logo_url: "", layout_width: "wide", sidebar_size: "comfortable", theme_preset: "midnight", density: "comfortable", corner_style: "soft", font_style: "modern" });

  useEffect(() => {
    api.get("/public-config").then((r) => setAppearance(r.data)).catch(() => {});
    if (!localStorage.getItem("mb_token")) return setAdmin(false);
    api.get("/auth/me").then((r) => setAdmin(r.data)).catch(() => setAdmin(false));
    const h = () => setAdmin(false);
    window.addEventListener("mb-logout", h);
    return () => window.removeEventListener("mb-logout", h);
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("mb_theme") || appearance.theme || "dark";
    document.documentElement.classList.toggle("dark", saved === "dark");
    document.documentElement.style.setProperty("--gold", hexToHsl(appearance.primary_color));
    document.documentElement.style.setProperty("--accent", hexToHsl(appearance.primary_color));
    document.documentElement.style.setProperty("--ring", hexToHsl(appearance.primary_color));
    document.documentElement.style.setProperty("--chart-1", hexToHsl(appearance.primary_color));
    document.documentElement.dataset.layout = appearance.layout_width || "wide";
    document.documentElement.dataset.sidebar = appearance.sidebar_size || "comfortable";
    document.documentElement.dataset.preset = appearance.theme_preset || "midnight";
    document.documentElement.dataset.density = appearance.density || "comfortable";
    document.documentElement.dataset.corners = appearance.corner_style || "soft";
    document.documentElement.dataset.font = appearance.font_style || "modern";
  }, [appearance]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("mb_token", data.token);
    setAdmin(data.admin);
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("mb_token");
    setAdmin(false);
  };
  const toggleTheme = () => {
    const dark = !document.documentElement.classList.contains("dark");
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("mb_theme", dark ? "dark" : "light");
  };
  return <AuthCtx.Provider value={{ admin, login, logout, appearance, setAppearance, toggleTheme }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
